import { AccountKind } from "../Account/AccountKind";

export enum FeatureFlagKey {
  Snowy = "snowy",
  Ticketing = "ticketing",
  TicketingGroups = "ticketing_groups",
  // The new Sequences engine (docs/specs/new-sequence-approach/engine-gates.md): one master
  // plus one flag per module, kept contiguous and in pipeline order — the Feature Flags
  // admin renders registry order, so this run reads as one group there.
  BotSequenceEngineEnabled = "bot_sequence_engine_enabled",
  BotSequenceEngineReconciler = "bot_sequence_engine_reconciler",
  BotSequenceEngineScheduler = "bot_sequence_engine_scheduler",
  BotSequenceEngineDispatcher = "bot_sequence_engine_dispatcher",
  BotSequenceEngineJitLookup = "bot_sequence_engine_jit_lookup",
  // Visibility of the new Sequences surfaces in Studio (ENG-3542) — UI only, the engine
  // flags above decide what runs. Kept next to them so the admin shows them as one group.
  SequencesPage = "sequences_page",
  PolicyBuilder = "policy_builder",
  Coaching = "coaching",
  HideLegacyNav = "hide_legacy_nav",
  Pulse = "pulse",
  // The Outbound Gate (ENG-3373): one per outbound stream. Key present = the org may send on that
  // stream (production: to everyone; elsewhere: to allowlisted recipients). The level has no meaning.
  OutboundTexts = "outbound_texts",
  OutboundCalls = "outbound_calls",
  OutboundEmails = "outbound_emails",
  OutboundFms = "outbound_fms",
  OutboundStaffPush = "outbound_staff_push",
  FmsSyncCortexVars = "fms_sync_cortex_vars",
  // SSM Cloud current rent (ENG-2446, C-13): opt-OUT flags — key present = the location sync stops
  // the call(s). Kept next to FmsSyncCortexVars so the admin shows the FMS-sync flags as one group.
  SyncSsmCloudNewRentCallsDisabled = "sync_ssm_cloud_new_rent_calls_disabled",
  SyncSsmCloudNewRentCallsInsuranceDisabled = "sync_ssm_cloud_new_rent_calls_insurance_disabled",
  GoogleReviews = "google_reviews",
  /** The spot tenant page (ENG-3545): the page itself and every sender that links to it. Absent = off. */
  TenantPage = "tenant_page"
}

export interface FeatureFlagDef {
  key: FeatureFlagKey;
  label: string;
  description: string;
}

export const FEATURE_FLAG_REGISTRY: Record<FeatureFlagKey, FeatureFlagDef> = {
  [FeatureFlagKey.Snowy]: {
    key: FeatureFlagKey.Snowy,
    label: "Snowy",
    description: "Snowy stack: AI Studio, Contact Center - NEW, PM - NEW, Escalation & Ticketing"
  },
  [FeatureFlagKey.Ticketing]: {
    key: FeatureFlagKey.Ticketing,
    label: "Ticketing",
    description: "Shows the Ticketing page in the main nav."
  },
  [FeatureFlagKey.TicketingGroups]: {
    key: FeatureFlagKey.TicketingGroups,
    label: "Ticketing groups",
    description: "Ticketing groups (reserved; not yet wired to a surface)."
  },
  [FeatureFlagKey.BotSequenceEngineEnabled]: {
    key: FeatureFlagKey.BotSequenceEngineEnabled,
    label: "Sequences engine (master)",
    description:
      "The new Sequences engine for this org. Master switch: every Sequences module flag below needs it. Absent = no module touches the org, even rows it already has."
  },
  [FeatureFlagKey.BotSequenceEngineReconciler]: {
    key: FeatureFlagKey.BotSequenceEngineReconciler,
    label: "Sequences engine: Reconciler",
    description: "Change capture, reconciler enrollments and the bootstrap backfill for the org. Needs the master flag. Absent = enrollments frozen."
  },
  [FeatureFlagKey.BotSequenceEngineScheduler]: {
    key: FeatureFlagKey.BotSequenceEngineScheduler,
    label: "Sequences engine: Scheduler",
    description: "Books scheduled attempts for the org's enrollments. Needs the master flag. Absent = nothing books."
  },
  [FeatureFlagKey.BotSequenceEngineDispatcher]: {
    key: FeatureFlagKey.BotSequenceEngineDispatcher,
    label: "Sequences engine: Dispatcher",
    description: "Claims and fires the org's booked attempts — the SENDS switch. Needs the master flag. Absent = nothing sends."
  },
  [FeatureFlagKey.BotSequenceEngineJitLookup]: {
    key: FeatureFlagKey.BotSequenceEngineJitLookup,
    label: "Sequences engine: JIT lookup",
    description:
      "Before a sequence fire, refresh the tenant's lease or lead from the FMS when the location's cache is stale. Needs the master flag; the per-integration JIT settings on the FMS page still apply. Absent = fire on the cached data."
  },
  [FeatureFlagKey.SequencesPage]: {
    key: FeatureFlagKey.SequencesPage,
    label: "Sequences page",
    description:
      "Shows the new Sequences surfaces on a Snowy org: the Sequences tab in Property Manager - NEW and the Sequences / Sequence Suppressions pages in CRM. The level says who sees them (Swivl Staff = hidden from customers). Gates the UI only; the engine keeps running on its own flags. The Property Manager tab also needs the master, Reconciler, Scheduler and Dispatcher flags present."
  },
  [FeatureFlagKey.PolicyBuilder]: {
    key: FeatureFlagKey.PolicyBuilder,
    label: "Policy worksheets",
    description:
      "Operator-facing policy worksheet screens (structured policies replacing free text). Gates the UI only — the bot's policy read path is unconditional."
  },
  [FeatureFlagKey.Coaching]: {
    key: FeatureFlagKey.Coaching,
    label: "Coaching (AI Assistance)",
    description:
      "Contact Center coaching: conversation score, live nudges, AI Assistance panel. Key present = the engine runs for the org's conversations; the threshold gates who sees the UI. Missing = no coaching ticks run."
  },
  [FeatureFlagKey.HideLegacyNav]: {
    key: FeatureFlagKey.HideLegacyNav,
    label: "Hide legacy nav",
    description:
      "For orgs also on Snowy: hides the legacy Contact Center and Property Manager nav items (leaving only the V3 entries) and the legacy Settings pages — Sequences, Escalation Groups, Escalation History, Workflows — whose URLs land on Settings."
  },
  [FeatureFlagKey.Pulse]: {
    key: FeatureFlagKey.Pulse,
    label: "Pulse",
    description:
      "Pulse landing page. Key present, with Snowy on too = the Pulse agent keeps a page ready for every member with a persona; the threshold decides who sees the page. Missing, or no Snowy = no Pulse runs."
  },
  [FeatureFlagKey.OutboundTexts]: {
    key: FeatureFlagKey.OutboundTexts,
    label: "Outbound: texts",
    description:
      "Outbound Gate — the org may send texts (bot replies, sequences, starters, campaigns, verification codes, staff texts). Off = every text is simulated. In production this alone decides; elsewhere the recipient must also be on the Outbound Allowlist."
  },
  [FeatureFlagKey.OutboundCalls]: {
    key: FeatureFlagKey.OutboundCalls,
    label: "Outbound: calls",
    description: "Outbound Gate — the org may place calls (sequence calls, the contact-center dialer). Off = every call is simulated."
  },
  [FeatureFlagKey.OutboundEmails]: {
    key: FeatureFlagKey.OutboundEmails,
    label: "Outbound: emails",
    description: "Outbound Gate — the org may send emails (sequence emails, staff notification emails). Off = every email is simulated."
  },
  [FeatureFlagKey.OutboundFms]: {
    key: FeatureFlagKey.OutboundFms,
    label: "Outbound: FMS updates",
    description:
      "Outbound Gate — the org may write into its FMS (tenant notes, leads, payments). Off = notes and leads are simulated and payments fail."
  },
  [FeatureFlagKey.OutboundStaffPush]: {
    key: FeatureFlagKey.OutboundStaffPush,
    label: "Outbound: staff push",
    description: "Outbound Gate — the org's members may receive browser push notifications. Off = simulated."
  },
  [FeatureFlagKey.FmsSyncCortexVars]: {
    key: FeatureFlagKey.FmsSyncCortexVars,
    label: "FMS variables: Cortex V3",
    description:
      "Reads the location's FMS integration id, facility id, sync enablement and timezone from the Cortex V3 variables instead of the legacy ones — for FMS sync, payments, gate code, lead push, FMS notes, the widget account and the new Sequences engine. Key present = on for the whole org, whatever account kind is selected; no fallback to legacy values."
  },
  [FeatureFlagKey.SyncSsmCloudNewRentCallsDisabled]: {
    key: FeatureFlagKey.SyncSsmCloudNewRentCallsDisabled,
    label: "Sync SSMCloud: disable getting current rent, autopay and insurance",
    description:
      "Opt-out. Key present = the SSM Cloud location sync stops calling GetCustomerInfo_V3 and GetUnitsWithInsurance for this org; stored rent, autopay and insurance premium are kept as last synced, new leases get the move-in rent. Absent = on (default). The level has no meaning."
  },
  [FeatureFlagKey.SyncSsmCloudNewRentCallsInsuranceDisabled]: {
    key: FeatureFlagKey.SyncSsmCloudNewRentCallsInsuranceDisabled,
    label: "Sync SSMCloud: disable getting insurance fee",
    description:
      "Opt-out. Key present = the sync skips GetUnitsWithInsurance for this org; stored premiums are kept; current rent and autopay still sync. Absent = on (default). The level has no meaning."
  },
  [FeatureFlagKey.GoogleReviews]: {
    key: FeatureFlagKey.GoogleReviews,
    label: "Google Reviews",
    description:
      "Master switch for Google review sync and auto-replies on the org. Key present (any kind) = the sync and auto-reply jobs run; missing = nothing is synced or replied, whatever the per-location Google review variables say. Does not gate the Reviews page."
  },
  [FeatureFlagKey.TenantPage]: {
    key: FeatureFlagKey.TenantPage,
    label: "spot tenant page",
    description:
      "The spot.is tenant page (my.spot.is/<brand>/user/<id>): tenants see their units and balances and pay. Present = the page works and past-due texts, widget Pay Now and the bot's pay action link to it (each location can still be switched off in Property Manager). Absent = the page refuses and every sender falls back to the portal URL."
  }
};

// Per-org map: each key maps to the *minimum AccountKind* required to see
// that feature on the org. Missing key = feature is off on that org.
// Account ordering (low → high): Customer < SwivlStaff < SwivlAdmin.
export type FeatureFlagMap = Partial<Record<FeatureFlagKey, AccountKind>>;
